import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, Empty } from "@/components/ui-kit";
import { STATUS_BADGE, label, todayStr } from "@/components/Pager";
import { DiaryReports } from "@/components/DiaryReports";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Users, ListChecks, AlertTriangle, BookOpenText, Eye, MessageCircle } from "lucide-react";

function TeacherTable({ rows }) {
  return (
    <div className="bg-white rounded-2xl border border-violet-100 overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-violet-50"><tr><th className="text-left px-3 py-2.5">Teacher</th><th className="text-left px-3">Classes & diary status</th><th className="px-3">Tasks</th><th className="px-3">Done</th><th className="px-3">Pending</th><th className="px-3">Overdue</th><th className="px-3">Completion</th></tr></thead>
        <tbody className="divide-y divide-slate-100">
          {rows.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-slate-500">No teachers found.</td></tr>}
          {rows.map((t) => (
            <tr key={t.id} data-testid={`monitor-teacher-${t.id}`}>
              <td className="px-3 py-2.5 font-semibold">{t.name}</td>
              <td className="px-3 py-2"><div className="flex flex-wrap gap-1.5">{t.classes.length === 0 ? <span className="text-xs text-slate-400">No class assigned</span> : t.classes.map((c) => (
                <Badge key={c.class_name + c.section} className={`${STATUS_BADGE[c.status]} capitalize`}>{c.class_name}-{c.section}: {label(c.status)}{c.status === "published" ? ` • ${c.acks} seen` : ""}</Badge>))}</div></td>
              <td className="text-center px-3">{t.tasks_total}</td><td className="text-center px-3 text-emerald-700">{t.tasks_completed}</td>
              <td className="text-center px-3 text-amber-700">{t.tasks_pending}</td><td className="text-center px-3 text-rose-700">{t.tasks_overdue}</td>
              <td className="text-center px-3 font-bold">{t.completion_pct == null ? "—" : `${t.completion_pct}%`}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function MonitorPage() {
  const [day, setDay] = useState(todayStr());
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/monitor/teachers", { params: { day } }).then((r) => setD(r.data)).catch((e) => toast.error(formatApiError(e))); }, [day]);
  const k = d?.totals || {};
  return (
    <AppShell>
      <PageHeader eyebrow="Principal" title="Teacher Monitoring" subtitle="Daily task completion, diary submissions and parent acknowledgments.">
        <Input data-testid="monitor-day" type="date" value={day} onChange={(e) => setDay(e.target.value)} className="w-44 bg-white" />
      </PageHeader>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mb-5">
        {[["Teachers", k.teachers, Users], ["Tasks today", k.tasks, ListChecks], ["Completed", k.completed, ListChecks], ["Overdue", k.overdue, AlertTriangle], ["Diaries published", k.diaries_published, BookOpenText], ["Parent views", k.acks, Eye]].map(([l, v, I]) => (
          <div key={l} data-testid={`monitor-kpi-${l.toLowerCase().replace(/\s/g, "-")}`} className="bg-white rounded-2xl shadow-sm p-3"><I size={16} className="text-violet-600" /><div className="font-display text-2xl font-extrabold mt-1">{v ?? 0}</div><div className="text-xs text-slate-500">{l}</div></div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mb-5">
        <Button asChild className="btn-brand" data-testid="monitor-assign-task"><Link to="/tasks">Assign / review tasks</Link></Button>
        <Button asChild variant="outline" data-testid="monitor-pending-approval"><Link to="/diary?status=pending_approval">Diaries waiting approval ({d?.pending_approval ?? 0})</Link></Button>
        <Button asChild variant="outline" data-testid="monitor-communication"><Link to="/messages"><MessageCircle size={15} className="mr-1" /> Teacher–parent messages</Link></Button>
      </div>
      <Tabs defaultValue="teachers">
        <TabsList className="bg-violet-100 flex-wrap h-auto">
          <TabsTrigger value="teachers" data-testid="monitor-tab-teachers">Teachers</TabsTrigger>
          <TabsTrigger value="missing" data-testid="monitor-tab-missing">Not submitted ({d?.missing.length ?? 0})</TabsTrigger>
          <TabsTrigger value="reports" data-testid="monitor-tab-reports">Reports</TabsTrigger>
        </TabsList>
        <TabsContent value="teachers" className="mt-4">{d && <TeacherTable rows={d.teachers} />}</TabsContent>
        <TabsContent value="missing" className="mt-4">
          {!d || d.missing.length === 0 ? <Empty testid="monitor-missing-empty" icon={BookOpenText} text="Every assigned class has a diary for this day." /> : (
            <div className="bg-white rounded-2xl shadow-sm divide-y divide-slate-100">
              {d.missing.map((m) => (
                <div key={m.teacher_id + m.class_name + m.section} data-testid={`monitor-missing-${m.teacher_id}-${m.class_name}`} className="px-4 py-3 flex items-center gap-3">
                  <span className="font-semibold flex-1">{m.teacher_name}</span><span className="text-sm">{m.class_name} - {m.section}</span>
                  <Badge className={STATUS_BADGE[m.status]}>{m.status === "draft" ? "Draft only" : "Not submitted"}</Badge>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
        <TabsContent value="reports" className="mt-4"><DiaryReports /></TabsContent>
      </Tabs>
    </AppShell>
  );
}
